import ChartBarLineIcon from '@atlaskit/icon-lab/core/chart-bar-line';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import { useCallback, useMemo } from 'react';
import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	type BackendMessageStageInput,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--post-office-team/rovo-nudge';
import { NudgeDefinition } from '@post-office/placements--post-office-team/rovo-nudge/nudge-definition';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../../index';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		validate: assertMessageTemplateContext,
		stage: async ({
			message,
		}: BackendMessageStageInput<MessageTemplateContext>): Promise<MessageTemplateContext> => {
			return message.context;
		},
	},
}));

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: assertMessageTemplateContext,
		Component: ({ title, description }: MessageTemplateContext) => {
			const icon = useCallback(() => <ChartBarLineIcon size="medium" label={title} />, [title]);
			const nudgeContext = useMemo(() => ({ id: 'live-nudge-demo-message' }), []);

			return (
				<NudgeDefinition
					title={title}
					description={description}
					icon={icon}
					handleType={HandleActionType.Chat}
					staticPrompt={`Tell me more about: ${title}`}
					starterType="static"
					nudgeContext={nudgeContext}
				/>
			);
		},
	},
});
